import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { Download, X, Share } from 'lucide-react';

const PWAInstallPrompt = () => {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [show, setShow] = useState(false);
  const [isIos, setIsIos] = useState(false);
  const [installing, setInstalling] = useState(false);

  useEffect(() => {
    const ua = window.navigator.userAgent.toLowerCase();
    const ios = /iphone|ipad|ipod/.test(ua);

    const inStandaloneIos = window.navigator.standalone === true;
    const inStandaloneAndroid = !ios && window.matchMedia('(display-mode: standalone)').matches;

    if (inStandaloneIos || inStandaloneAndroid) return;

    setIsIos(ios);

    if (ios) {
      setTimeout(() => setShow(true), 2000);
      return;
    }

    let prompted = false;

    const handler = (e) => {
      e.preventDefault();
      prompted = true;
      setDeferredPrompt(e);
      setTimeout(() => setShow(true), 2000);
    };

    const fallbackTimer = setTimeout(() => {
      if (!prompted) setShow(true);
    }, 3000);

    window.addEventListener('beforeinstallprompt', handler);
    window.addEventListener('appinstalled', () => setShow(false));

    return () => {
      window.removeEventListener('beforeinstallprompt', handler);
      clearTimeout(fallbackTimer);
    };
  }, []);

  const handleInstall = async () => {
    if (!deferredPrompt) return;
    setInstalling(true);
    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    if (outcome === 'accepted') setShow(false);
    setInstalling(false);
    setDeferredPrompt(null);
  };

  const handleDismiss = () => setShow(false);

  if (!show) return null;

  const isMobile = window.innerWidth < 992;

  const prompt = (
    <div
      style={{
        position: 'fixed',
        left: 0,
        right: 0,
        // Mobile: above our app's bottom nav (68px) + iOS safe area
        // Desktop: 16px from bottom
        bottom: isMobile
          ? 'calc(68px + env(safe-area-inset-bottom, 8px))'
          : '16px',
        zIndex: 2147483647, // max possible z-index
        padding: '0 12px',
        pointerEvents: 'none',
      }}
    >
      <div
        style={{
          maxWidth: '480px',
          margin: '0 auto',
          pointerEvents: 'auto',
          background: '#111827',
          borderRadius: '16px',
          boxShadow: '0 8px 32px rgba(0,0,0,0.35)',
          overflow: 'hidden',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '12px 16px' }}>
          <img
            src="/images/omahlogoupdated.png"
            alt="Omah Global"
            style={{ width: 44, height: 44, borderRadius: 12, objectFit: 'contain', background: '#fff', padding: 4, flexShrink: 0 }}
          />

          <div style={{ flex: 1, minWidth: 0 }}>
            <p style={{ color: '#fff', fontWeight: 600, fontSize: 14, margin: 0, lineHeight: 1.3 }}>Omah Global</p>
            {isIos ? (
              <p style={{ color: '#9ca3af', fontSize: 12, margin: '2px 0 0', lineHeight: 1.4 }}>
                Tap the <span style={{ color: '#60a5fa' }}>Share</span> icon then{' '}
                <strong style={{ color: '#fff' }}>"Add to Home Screen"</strong>
              </p>
            ) : deferredPrompt ? (
              <p style={{ color: '#9ca3af', fontSize: 12, margin: '2px 0 0' }}>Install app to your device</p>
            ) : (
              <p style={{ color: '#9ca3af', fontSize: 12, margin: '2px 0 0', lineHeight: 1.4 }}>
                Tap <strong style={{ color: '#fff' }}>⋮</strong> then{' '}
                <strong style={{ color: '#fff' }}>"Add to Home Screen"</strong>
              </p>
            )}
          </div>

          {!isIos && deferredPrompt && (
            <button
              onClick={handleInstall}
              disabled={installing}
              style={{
                display: 'flex', alignItems: 'center', gap: 6,
                background: '#fff', color: '#111', fontSize: 12, fontWeight: 700,
                padding: '8px 14px', borderRadius: 12, border: 'none', cursor: 'pointer',
                flexShrink: 0, opacity: installing ? 0.6 : 1,
              }}
            >
              <Download size={13} />
              {installing ? 'Installing…' : 'Install'}
            </button>
          )}

          <button
            onClick={handleDismiss}
            style={{ padding: 4, background: 'none', border: 'none', cursor: 'pointer', color: '#6b7280', flexShrink: 0 }}
            aria-label="Dismiss"
          >
            <X size={16} />
          </button>
        </div>

        {(isIos || (!deferredPrompt && !isIos)) && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '0 16px 12px' }}>
            <div style={{ flex: 1, height: 1, background: '#374151' }} />
            <p style={{ color: '#6b7280', fontSize: 11, margin: 0, whiteSpace: 'nowrap' }}>
              {isIos ? 'Use Safari browser to install' : 'Use your browser menu to install'}
            </p>
            <div style={{ flex: 1, height: 1, background: '#374151' }} />
          </div>
        )}
      </div>
    </div>
  );

  return ReactDOM.createPortal(prompt, document.body);
};

export default PWAInstallPrompt;
